import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import PageLayout from '../components/PageLayout';
import Modal from '../components/Modal';
import client from '../api/client';
import { abandonAssessment } from '../api/assessment';
import ProfileNudgeModal from '../components/ProfileNudgeModal';
import useProfileGuard from '../hooks/useProfileGuard';
import Alert from '../components/Alert';
import HomeIconSprite from '../components/home/HomeIconSprite';
import MaturityMatrix from '../components/home/MaturityMatrix';
import DecisionFlow from '../components/home/DecisionFlow';
import ladderSvg from '../assets/leadership-ladder.svg';
import './HomePage.css';

// 五力 × 四阶矩阵数据。FORCES 仅本页展示用；文案对齐参考图/正式术语（惯用/扩散/谋势）。
// 五力平行独立、无高低之分；阶梯图与「五层」仅为品牌展示排列，不代表评分前置或递进。
// cells = [初用, 惯用, 扩散, 重构] 四阶在该力上的简短表现。
const FORCES = [
  { code: 'ai_leverage', name: 'AI 借力', sub: '高频管理动作', sym: 's-wand',
    cells: ['偶尔借助 AI 处理个别管理动作', '稳定把 AI 嵌入高频管理动作', '带动下属把 AI 用进管理动作', '推动管理层整体重塑工作方式'] },
  { code: 'ai_symbiosis', name: 'AI 共生', sub: '人机复合管理者', sym: 's-symbiosis',
    cells: ['初步觉察自身的人机共生状态', '稳定切换人机协作模式，关键判断不外包', '向团队示范清醒的人机协作状态', '推动组织重定义管理者能力构成'] },
  { code: 'ai_team_leadership', name: 'AI 带队', sub: '人机混合团队', sym: 's-team',
    cells: ['识别团队中的 AI 增强员工', '稳定管理混合团队并差异化激发', '重构岗位、绩效、培训与转岗', '推动事业部进入“人＋AI”组织形态'] },
  { code: 'ai_governance', name: 'AI 治理', sub: '智能体与数字员工', sym: 's-gov',
    cells: ['试用过智能体或数字员工', '为常用 AI 主体设计目标、护栏与问责', '建立组织级 AI 主体治理框架', '推动企业建立 AI 主体身份与责任体系'] },
  { code: 'ai_strategic_foresight', name: 'AI 谋势', sub: '外部变局与战略判断', sym: 's-fore',
    cells: ['关注 AI 趋势并形成初步认知', '解读 AI 重塑行业、客户与竞争的信号', '基于 AI 时局判断推动业务调整', '推动企业完成 AI 时代的战略级转型'] },
];

export default function HomePage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  // dialog: null | 'resume' | 'discard-confirm'
  const [dialog, setDialog] = useState(null);
  const [unfinished, setUnfinished] = useState(null);
  const [busy, setBusy] = useState(false);          // abandon → start 流程的禁用态
  const [starting, setStarting] = useState(false);  // CTA 点击后 profile/resume 并发查询
  const [dialogErr, setDialogErr] = useState('');
  const [fetchErr, setFetchErr] = useState('');
  const { guardOpen, checkAndGuard, closeGuard } = useProfileGuard();

  const handleStart = async () => {
    // 防双击：查询期间再次点击直接忽略
    if (starting) return;

    // ① 未登录 → 直跳注册（产品决定 A）
    if (!user) { navigate('/register'); return; }

    // ② 已登录 → 整个流程包外层 try/finally,确保 guard 等待期间按钮也 disabled
    setStarting(true); setFetchErr('');
    try {
      let resume = null;
      try {
        const { data } = await client.get('/assessments/resume/');
        resume = data;
      } catch (err) {
        // 401：让 axios 拦截器去处理 token 刷新 / 跳 /login，本组件不要再 navigate
        // 抢路由（拦截器在 refresh 失败时会执行 window.location.href = '/login'）。
        // 其它错误（5xx 等）才跳规则页让那边的现有兜底接手。
        if (err?.response?.status === 401) return;
        navigate('/assessment/rules');
        return;
      }

      // 有未完成测评 → Modal 让用户选「继续 / 开新」
      // 注：dialog 里点 [继续上次] 直接 navigate,不被 guard 拦
      //     dialog 里点 [开始新测评 → 丢弃旧] 走 handleDiscardOldAndStartNew,那里再 guard
      if (resume?.has_unfinished && resume.assessment) {
        setUnfinished(resume.assessment);
        setDialog('resume');
        return;
      }

      // 准备开新测评 → guard
      const status = await checkAndGuard();
      if (status === 'blocked_incomplete') return;          // hard modal 已弹
      if (status === 'blocked_auth') return;                // 让 axios 拦截器跳 /login
      if (status === 'blocked_error') {
        setFetchErr('暂时无法读取画像，请稍后重试');
        return;
      }
      // pass → 走深度选择页
      navigate('/assessment/rules');
    } finally {
      setStarting(false);   // 所有分支退出时统一清 starting
    }
  };

  // 彻底关闭弹窗链（× / 遮罩 / ESC）：把上下文清空回到首页。
  // discard-confirm 的「取消」按钮另走 backToResume，回到上一层而不是关掉整条。
  const closeDialog = () => {
    if (busy) return;
    setDialog(null);
    setDialogErr('');
  };

  const backToResume = () => {
    if (busy) return;
    setDialogErr('');
    setDialog('resume');
  };

  const handleDiscardOldAndStartNew = async () => {
    if (!unfinished?.id) return;
    setBusy(true); setDialogErr('');
    try {
      // guard 必须早于 abandon —— 防止用户被 gate 拦下时旧测评已经 abandon
      const status = await checkAndGuard();
      if (status === 'blocked_incomplete') {
        setDialog(null);          // 关 discard-confirm,避免与 hard gate 同 z-index (9999) 层叠
        return;                    // hard gate modal 自己弹出
      }
      if (status === 'blocked_auth') return;   // 让 axios 拦截器跳 /login
      if (status === 'blocked_error') {
        setDialogErr('暂时无法读取画像，请稍后重试');   // 错误显示在 discard-confirm 内(用户视线焦点)
        return;
      }

      // pass → 执行 abandon
      try {
        await abandonAssessment(unfinished.id);
      } catch (err) {
        // 后端 abandon 已是幂等：abandoned 再调返回 200。
        // 只有真正的失败（网络断、500 等）才落到这条分支。
        setDialogErr(err.response?.data?.detail || '操作失败，请稍后再试');
        return;
      }
      setDialog(null);
      setUnfinished(null);
      navigate('/assessment/rules');
    } finally {
      setBusy(false);   // 所有分支退出都清 busy,避免按钮卡 "处理中"
    }
  };

  // 第二个 CTA 按登录态分文案（产品决定）
  const secondaryCtaText = user ? '继续测评' : '立即注册开始测评';

  return (
    <PageLayout>
      <div className="home-v3">
        <HomeIconSprite />
        <ProfileNudgeModal />
        <ProfileNudgeModal mode="hard" open={guardOpen} onClose={closeGuard} />

        {/* ===== HERO ===== */}
        <section className="hero">
          <h1>AIQ</h1>
          <h2>前哨 AI 领导力测评系统</h2>
          <p className="hero-sub">
            基于<b>前哨 AI 领导力模型</b>，评估管理者在 <b>AI 借力</b>、<b>AI 共生</b>、<b>AI 带队</b>、<b>AI 治理</b>、<b>AI 谋势</b>五层中的成熟度，<br />
            并将 AI 领导力转化为<b>可诊断、可培养、可追踪</b>的管理能力。
          </p>
          <div className="hero-fig">
            <img src={ladderSvg} alt="前哨 AI 领导力模型 · 五层领导力与四阶成熟度阶梯图" />
          </div>
          {fetchErr && (
            <div style={{ marginBottom: 12, maxWidth: 480, marginLeft: 'auto', marginRight: 'auto' }}>
              <Alert type="error">{fetchErr}</Alert>
            </div>
          )}
          <button className="btn" onClick={handleStart} disabled={starting}>
            {starting ? '处理中…' : '开始测评 →'}
          </button>
        </section>

        {/* ===== 5×4 成熟度矩阵 ===== */}
        <MaturityMatrix forces={FORCES} />

        {/* ===== 决策流程 ===== */}
        <DecisionFlow />

        {/* ===== 底部 CTA ===== */}
        <section className="cta">
          <h2>准备好了吗？</h2>
          <p>测评结果将生成专属 AIQ 报告 · 支持下载</p>
          <button className="btn" onClick={handleStart} disabled={starting}>
            {starting ? '处理中…' : `${secondaryCtaText} →`}
          </button>
        </section>

        {/* —— 弹窗:有未完成测评,选继续 / 开新 —— */}
        <Modal
          open={dialog === 'resume'}
          onClose={closeDialog}
          title="你有一次未完成的测评"
          footer={
            <>
              <button onClick={closeDialog} style={btnGhost}>关闭</button>
              <button onClick={() => setDialog('discard-confirm')} style={btnGhostDanger}>开始新测评</button>
              <button
                onClick={() => {
                  if (!unfinished?.id) return;
                  setDialog(null);
                  navigate(`/assessment/${unfinished.id}`);
                }}
                style={btnPrimary}
              >
                继续上次
              </button>
            </>
          }
        >
          <p style={modalP}>
            你之前还有一次测评没有完成。
            可以从上次的进度继续作答，或开始一次全新的测评。
          </p>
        </Modal>

        {/* —— 弹窗：开新测评 二次确认（点 × / 遮罩 / ESC 整链关闭；点「取消」回上一层） —— */}
        <Modal
          open={dialog === 'discard-confirm'}
          onClose={closeDialog}
          title="确认开始新测评？"
          closeOnBackdrop={!busy}
          closeOnEsc={!busy}
          footer={
            <>
              <button onClick={backToResume} disabled={busy} style={btnGhost}>取消</button>
              <button
                onClick={handleDiscardOldAndStartNew}
                disabled={busy}
                style={busy ? btnPrimaryDisabled : btnDanger}
              >
                {busy ? '处理中...' : '丢弃并开始新测评'}
              </button>
            </>
          }
        >
          <p style={modalP}>
            开始新测评后，上次未完成测评的进度<strong style={{ color: '#dc2626' }}>将被丢弃</strong>且无法恢复。
            确认要继续吗？
          </p>
          {dialogErr && (
            <div style={{ marginTop: 12, padding: '8px 10px', background: '#fef2f2', color: '#dc2626', fontSize: 13, borderRadius: 4 }}>
              {dialogErr}
            </div>
          )}
        </Modal>
      </div>
    </PageLayout>
  );
}

const btnBase = {
  padding: '10px 18px', border: 'none', borderRadius: 6,
  fontSize: 14, fontWeight: 600, cursor: 'pointer',
};
const btnGhost = { ...btnBase, background: '#f3f4f6', color: '#475569' };
const btnGhostDanger = { ...btnBase, background: 'transparent', color: '#dc2626', border: '1px solid #fecaca' };
const btnPrimary = { ...btnBase, background: '#2563eb', color: '#fff' };
const btnPrimaryDisabled = { ...btnPrimary, background: '#cbd5e1', cursor: 'not-allowed' };
const btnDanger = { ...btnBase, background: '#dc2626', color: '#fff' };
const modalP = { color: '#475569', fontSize: 14, margin: '0 0 4px', lineHeight: 1.7 };
